import {useEffect, useState, useCallback, useMemo, useRef} from 'react'; 
import {
    fetchSummaryMetrics,
    fetchSummaryChart,
    fetchSummaryTable,
    fetchMetricOptions,
    fetchFilterOptions
} from '@/services/api' 
import { formatValue } from '@/lib/post_analysis_utils/formatValue';

function buildFilterArrays(campaignName, poc_id, poc_name) {
    const campaignArray = Array.isArray(campaignName)
        ? campaignName
        : (campaignName ? [campaignName] : []);

    const pocIdArray = Array.isArray(poc_id)
        ? poc_id
        : (poc_id ? [poc_id] : []);

    const pocNameArray = Array.isArray(poc_name)
        ? poc_name
        : (poc_name ? [poc_name] : []);

    return { campaignArray, pocIdArray, pocNameArray };
}

export const useGetSummaryData = (region, brand, campaignName, start_date, end_date, poc_id, poc_name, key_account, context = "summary") => { 
    // States for summary metrics
    const [summaryMetrics, setSummaryMetrics] = useState(null);
    const [summaryChart, setSummaryChart] = useState(null);
    const [summaryTable, setSummaryTable] = useState(null);
    const [tablePage, setTablePage] = useState(1);
    const [tablePageSize, setTablePageSize] = useState(20);
    const [tableTotalPages, setTableTotalPages] = useState(1);
    const [metricOptions, setMetricOptions] = useState(null);
    const [filterOptions, setFilterOptions] = useState({
        region: null,
        brand: null,
        campaign_name: null,
        start_date: null,
        end_date: null,
        poc_id: null,
        key_account: null
    });
    
    // Loading and error states
    const [isLoading, setIsLoading] = useState(false);
    const [isTableLoading, setIsTableLoading] = useState(false);
    const [error, setError] = useState(null);

    // Memoized fetch functions
    const getSummaryMetrics = useCallback(async () => {
        try {
            setIsLoading(true);
            const { campaignArray, pocNameArray } = buildFilterArrays(campaignName, poc_id, poc_name);
            const data = await fetchSummaryMetrics(
                region,
                brand,
                campaignArray.length > 0 ? campaignArray : undefined,
                start_date,
                end_date,
                poc_id,
                pocNameArray.length > 0 ? pocNameArray : undefined,
                key_account
            );
            setSummaryMetrics(data);
            return data;
        } catch (err) {
            setError(err);
            throw err;
        } finally {
            setIsLoading(false);
        }
    }, [region, brand, campaignName, start_date, end_date, poc_id, poc_name, key_account]);

    const getSummaryChart = useCallback(async (xAxis, yAxis) => {
        try {
            setIsLoading(true);
            const { campaignArray, pocNameArray } = buildFilterArrays(campaignName, poc_id, poc_name);
            const data = await fetchSummaryChart(
                xAxis,
                yAxis,
                region,
                brand,
                campaignArray.length > 0 ? campaignArray : undefined,
                start_date,
                end_date,
                poc_id,
                pocNameArray.length > 0 ? pocNameArray : undefined,
                key_account
            );
            setSummaryChart(data);
            return data;
        } catch (error) {
            console.error('Error fetching summary chart:', error);
            setError(error);
            throw error;
        } finally {
            setIsLoading(false);
        }
    }, [region, brand, campaignName, start_date, end_date, poc_id, poc_name, key_account]);

    const getSummaryTable = useCallback(async (page = tablePage, page_size = tablePageSize) => {
        try {
            setIsTableLoading(true);
            const { campaignArray, pocNameArray } = buildFilterArrays(campaignName, poc_id, poc_name);
            const data = await fetchSummaryTable(
                region,
                brand,
                campaignArray.length > 0 ? campaignArray : undefined,
                start_date,
                end_date,
                poc_id,
                pocNameArray.length > 0 ? pocNameArray : undefined,
                key_account,
                page,
                page_size
            );
            setSummaryTable(data);
            setTablePage(data.page || 1);
            setTablePageSize(data.page_size || 20);
            setTableTotalPages(data.total_pages || 1);
            return data;
        } catch (error) {
            console.error('Error fetching summary table:', error);
            setError(error);
            throw error;
        } finally {
            setIsTableLoading(false);
        }
    }, [region, brand, campaignName, start_date, end_date, poc_id, poc_name, key_account, tablePage, tablePageSize]);

    // Fetch metric options (on mount)
    useEffect(() => {
        const fetchMetrics = async () => {
            try {
                const data = await fetchMetricOptions(context);
                setMetricOptions(data);
            } catch (err) {
                setError(err);
            }
        };
        fetchMetrics();
    }, [context]);

    const stringCompare = (a, b) => String(a).localeCompare(String(b), undefined, { sensitivity: 'base' });
    // Create stable string representation of poc_id array to prevent infinite loops
    const pocIdKey = useMemo(() => {
        const pocIdArray = Array.isArray(poc_id) ? poc_id : (poc_id ? [poc_id] : []);
        return JSON.stringify([...pocIdArray].sort(stringCompare));
    }, [poc_id]);

    // Create stable string representation of campaign array to prevent infinite loops
    const campaignKey = useMemo(() => {
        const campaignArray = Array.isArray(campaignName) ? campaignName : (campaignName ? [campaignName] : []);
        return JSON.stringify([...campaignArray].sort(stringCompare));
    }, [campaignName]);

    // Create stable string representation of poc_name array to prevent infinite loops
    const pocNameKey = useMemo(() => {
        const pocNameArray = Array.isArray(poc_name) ? poc_name : (poc_name ? [poc_name] : []);
        return JSON.stringify([...pocNameArray].sort(stringCompare));
    }, [poc_name]);

    // Track previous filter values to prevent unnecessary re-fetches
    const prevFiltersRef = useRef({});

    // Fetch filter options (on mount and when filters change)
    useEffect(() => {
        // Create a key to compare filter values
        const filterKey = JSON.stringify({
            region,
            brand,
            campaign: campaignKey,
            start_date,
            end_date,
            poc_id: pocIdKey,
            poc_name: pocNameKey,
            key_account
        });

        // Only fetch if filters actually changed (or on initial mount)
        if (prevFiltersRef.current.filterKey === filterKey && prevFiltersRef.current.filterKey !== undefined) {
            return;
        }

        prevFiltersRef.current.filterKey = filterKey;

        const fetchFilters = async () => {
            try {
                const { campaignArray, pocIdArray, pocNameArray } = buildFilterArrays(campaignName, poc_id, poc_name);
                const data = await fetchFilterOptions(
                    region,
                    brand,
                    campaignArray.length > 0 ? campaignArray : undefined,
                    start_date,
                    end_date,
                    pocIdArray.length > 0 ? pocIdArray : undefined,
                    pocNameArray.length > 0 ? pocNameArray : undefined,
                    key_account
                );
                setFilterOptions(data || {
                    region: null,
                    brand: null,
                    campaign_name: null,
                    start_date: null,
                    end_date: null,
                    poc_id: null,
                    key_account: null
                });
            } catch (err) {
                setError(err);
            }
        };
        fetchFilters();
    }, [region, brand, campaignKey, start_date, end_date, pocIdKey, pocNameKey, key_account]);

    // Fetch summary metrics when filters change
    useEffect(() => {
        getSummaryMetrics();
    }, [getSummaryMetrics]);

    // Fetch table data when filters change or page changes
    useEffect(() => {
        getSummaryTable(tablePage, tablePageSize);
    }, [tablePage, tablePageSize, getSummaryTable]);

    // Function to fetch all summary table data for export
    const getAllSummaryTable = async () => {
        const { campaignArray, pocNameArray } = buildFilterArrays(campaignName, poc_id, poc_name);
        const data = await fetchSummaryTable(
            region,
            brand,
            campaignArray.length > 0 ? campaignArray : undefined,
            start_date,
            end_date,
            poc_id,
            pocNameArray.length > 0 ? pocNameArray : undefined,
            key_account,
            1,
            100000
        );
        return data;
    };

    return {
        // Data
        summaryMetrics,
        summaryChart,
        summaryTable,
        metricOptions,
        filterOptions,
        
        // Loading and error states
        isLoading,
        isTableLoading,
        error,
        
        // Functions to fetch data
        getSummaryChart,
        getSummaryTable,
        getSummaryMetrics,
        getAllSummaryTable,
        
        // Pagination
        tablePage,
        setTablePage,
        tablePageSize,
        setTablePageSize,
        tableTotalPages
    };
}; 